// ABOUTME: Single source of truth for admin polling PATCH-allowed fields and update logic.
import { z } from 'zod';

type FieldApplyResult =
  | { ok: true; value: unknown }
  | { ok: false; error: string; statusCode: number };

type PollingPatchFieldDef = {
  schema: z.ZodOptional<z.ZodTypeAny>;
  apply: (value: unknown) => FieldApplyResult;
};

const booleanField = (): PollingPatchFieldDef => ({
  schema: z.boolean().optional(),
  apply: (value) => ({ ok: true, value }),
});

const stringField = (): PollingPatchFieldDef => ({
  schema: z.string().optional(),
  apply: (value) => ({ ok: true, value: (value as string).trim() }),
});

// Identity strings that pairing and workflows read — reject empty after trim.
const nonEmptyStringField = (fieldName: string): PollingPatchFieldDef => ({
  schema: z.string().optional(),
  apply: (value) => {
    const trimmed = (value as string).trim();
    if (trimmed.length === 0) {
      return {
        ok: false,
        error: `${fieldName} must not be empty`,
        statusCode: 400,
      };
    }
    return { ok: true, value: trimmed };
  },
});

const stringArrayField = (): PollingPatchFieldDef => ({
  schema: z.array(z.string()).optional(),
  apply: (value) => ({ ok: true, value }),
});

// Integer bounded to [min, max]. The bounds live in the schema so validation
// rejects out-of-range and non-numeric input before apply runs.
const numberField = (min: number, max: number): PollingPatchFieldDef => ({
  schema: z.number().int().min(min).max(max).optional(),
  apply: (value) => ({ ok: true, value: value as number }),
});

export const POLLING_PATCH_FIELD_MAP: Record<string, PollingPatchFieldDef> = {
  isDisabled: booleanField(),
  lastSync: {
    schema: z.string().optional(),
    apply: (value) => {
      const syncDate = new Date(value as string);
      if (isNaN(syncDate.getTime())) {
        return {
          ok: false,
          error: 'Invalid lastSync date format',
          statusCode: 400,
        };
      }
      return { ok: true, value: syncDate };
    },
  },
  operatorName: stringField(),
  originOperatorId: nonEmptyStringField('originOperatorId'),
  originUserId: nonEmptyStringField('originUserId'),
  registrations: {
    schema: z.array(z.string()).optional(),
    apply: (value) => {
      const cleaned = Array.from(
        new Set(
          (value as string[])
            .map((registration) => registration.trim())
            .filter((registration) => registration.length > 0),
        ),
      );

      if (cleaned.length > 500) {
        return {
          ok: false,
          error: 'registrations array exceeds 500 entries',
          statusCode: 400,
        };
      }

      return { ok: true, value: cleaned };
    },
  },
  daysToFetch: numberField(1, 89),
  useTransmormer: booleanField(),
  simplifiedMaintenanceItem: booleanField(),
  customRegistrationCode: stringField(),
  filterRegistrations: booleanField(),
  groupFlights: booleanField(),
  qualifications: stringArrayField(),
};

export const pollingPatchFieldsSchema = z.object(
  Object.fromEntries(
    Object.entries(POLLING_PATCH_FIELD_MAP).map(([key, fieldDef]) => [
      key,
      fieldDef.schema,
    ]),
  ),
);

export type PollingPatchFieldKey = keyof typeof POLLING_PATCH_FIELD_MAP;

export type PollingPatchFields = z.infer<typeof pollingPatchFieldsSchema>;

export const POLLING_PATCH_FIELD_KEYS = Object.keys(
  POLLING_PATCH_FIELD_MAP,
) as PollingPatchFieldKey[];

const POLLING_PATCH_FIELD_KEY_SET = new Set<PollingPatchFieldKey>(
  POLLING_PATCH_FIELD_KEYS,
);

export function isPersistablePollingField(
  key: string,
): key is PollingPatchFieldKey {
  return POLLING_PATCH_FIELD_KEY_SET.has(key as PollingPatchFieldKey);
}

export type ApplyPollingPatchResult =
  | { ok: true; updateData: Record<string, unknown> }
  | { ok: false; error: string; statusCode: number };

export function applyPollingPatchUpdates(
  updates: PollingPatchFields,
): ApplyPollingPatchResult {
  const updateData: Record<string, unknown> = {};

  for (const key of POLLING_PATCH_FIELD_KEYS) {
    const value = updates[key as keyof PollingPatchFields];
    if (value === undefined) {
      continue;
    }

    const fieldDef = POLLING_PATCH_FIELD_MAP[key];
    const result = fieldDef.apply(value);
    if (!result.ok) {
      return {
        ok: false,
        error: result.error,
        statusCode: result.statusCode,
      };
    }

    updateData[key] = result.value;
  }

  return { ok: true, updateData };
}
